/**
 * Die Diagramme des Lieferverzug-Reiters (recharts).
 *
 * Alle Diagramme arbeiten auf PERIODEN-Zeilen (KW / Monat / Jahr, frei
 * waehlbarer Zeitraum) — die Aggregation passiert in viz.ts, hier wird nur
 * gezeichnet.
 *
 * Regeln:
 * - EINE Y-Achse je Bild. Anzahl und Quote sind zwei Bilder untereinander,
 *   nie zwei Skalen in einem Plot.
 * - Raster/Achsen: durchgezogene Hairlines in Grau; gestrichelt ist fuer
 *   Datenserien reserviert (erste Meldung).
 * - Tooltips nennen Zaehler VOR Prozent ("3 von 15 (20 %)") — "20 % von 15"
 *   ohne Zaehler fand Matze zu Recht "quatsch" (18.08.).
 * - Vereinfachung 18.08.: Trend-Linie und Schwankungsband sind raus — vier
 *   Serien in einem Bild waren nicht mehr lesbar. Uebrig: letzte Meldung
 *   (blau) und erste Meldung (orange gestrichelt).
 */

import type { ReactNode } from 'react';
import {
  Bar, CartesianGrid, ComposedChart, LabelList, Line, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { useIstSchmal } from './bausteine';
import { FARBEN, periodeLang, prozent, tageMitWochen, zahl } from './viz';
import type {
  Granularitaet, PrognoseZeile, TreueZeile, VerschiebungAchse, VerschiebungZeile,
  VorlaufZeile, ZielZeile,
} from './viz';
import { kwLang } from './viz';

// ─── gemeinsame Bausteine ────────────────────────────────────────────────────

const ACHSE = {
  tick: { fontSize: 11, fill: FARBEN.tinteLeise },
  tickLine: false,
  axisLine: { stroke: FARBEN.linie },
} as const;

const RASTER = <CartesianGrid vertical={false} stroke={FARBEN.raster} strokeDasharray="0" />;

/**
 * Beide Bilder der Treue-Karte teilen sich diese Breite — nur so fluchten die
 * Perioden. Am Handy 2 px mehr: bei 44 px brach "100 %" auf zwei Zeilen um.
 * Die Zahl gilt fuer ALLE Karten, sonst fluchten die Diagramme nicht mehr
 * untereinander.
 */
const ACHSEN_BREITE = 44;
const ACHSEN_BREITE_SCHMAL = 50;

function TooltipRahmen({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <div style={{
      background: FARBEN.flaeche, border: `1px solid ${FARBEN.linie}`, borderRadius: '8px',
      padding: '0.6rem 0.75rem', fontSize: '0.75rem', color: FARBEN.tinteStark,
      boxShadow: '0 4px 14px rgba(26,26,26,0.10)', maxWidth: '300px', lineHeight: 1.55,
    }}>
      <div style={{ fontWeight: 650, marginBottom: '0.35rem' }}>{titel}</div>
      {children}
    </div>
  );
}

function Zeile({ farbe, label, wert }: { farbe?: string; label: string; wert: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', justifyContent: 'space-between' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: FARBEN.tinteMittel }}>
        {farbe && <span style={{ width: '9px', height: '9px', borderRadius: '2px', background: farbe, flexShrink: 0 }} />}
        {label}
      </span>
      <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{wert}</span>
    </div>
  );
}

/** "3 von 15 (20 %)" — Zaehler zuerst, Prozent in Klammern. */
function vonText(treffer: number, gesamt: number): string {
  if (gesamt === 0) return '—';
  return `${zahl(treffer)} von ${zahl(gesamt)} (${prozent(treffer / gesamt)})`;
}

/**
 * Punkt der Quotenlinie: duenne Basis hohl + Fallzahl, am Rand nach unten
 * ausweichen. Am Handy bleibt die Fallzahl weg (`ohneFallzahl`) — bei ~300 px
 * Diagrammbreite lagen die Zahlen auf der Linie. Hohl bleibt der Punkt, und
 * die Fallzahl steht weiter in der Tabellen-Ansicht und im Hinweis.
 */
function quotenPunkt(farbe: string, ohneFallzahl = false) {
  return (props: any) => {
    const { cx, cy, payload, index } = props;
    if (cx === undefined || cy === undefined || payload == null) return <g key={`leer-${index}`} />;
    const z: TreueZeile = payload;
    if (z.gesamt === 0) return <g key={`null-${index}`} />;
    const duenn = z.duenn;
    const obenEng = cy < 24;
    return (
      <g key={`punkt-${index}`}>
        <circle
          cx={cx} cy={cy} r={4}
          fill={duenn ? FARBEN.flaeche : farbe}
          stroke={duenn ? farbe : FARBEN.flaeche}
          strokeWidth={2}
        />
        {duenn && !ohneFallzahl && (
          <text x={cx} y={obenEng ? cy + 16 : cy - 10} textAnchor="middle" fontSize={9} fill={FARBEN.tinteLeise}>
            {z.gesamt === 1 ? '1 Fall' : `${z.gesamt} Fälle`}
          </text>
        )}
      </g>
    );
  };
}

// ─── 1. Termintreue: Anzahl je Periode + zwei Quotenlinien ───────────────────

/** Erklaerung fuer eine leere Periode: kein Import gelaufen ODER nichts geliefert. */
function leerText(periode: string, gran: Granularitaet, importWochen: string[], sonst: string): string {
  if (gran === 'kw' && importWochen.length > 0 && !importWochen.includes(periode)) {
    return 'In dieser Woche lief kein Import — Lieferungen dieser Woche erscheinen im nächsten Lauf.';
  }
  return sonst;
}

export function TreueDiagramm({ zeilen, gran, importWochen }: { zeilen: TreueZeile[]; gran: Granularitaet; importWochen: string[] }) {
  const schmal = useIstSchmal();
  const achsenBreite = schmal ? ACHSEN_BREITE_SCHMAL : ACHSEN_BREITE;
  return (
    <>
      {/* Bild 1: Wie viele Auftraege, und wie weit daneben? (Anzahl) */}
      <ResponsiveContainer width="100%" height={210}>
        <ComposedChart data={zeilen} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          {RASTER}
          {/* Perioden-Beschriftung steht EINMAL unter dem zweiten Bild. */}
          <XAxis dataKey="label" {...ACHSE} tick={false} height={8} />
          <YAxis {...ACHSE} allowDecimals={false} width={achsenBreite} />
          <Tooltip
            cursor={{ fill: 'rgba(26,26,26,0.04)' }}
            content={({ active, payload }: any) => {
              if (!active || !payload?.length) return null;
              const z: TreueZeile = payload[0].payload;
              if (z.gesamt === 0) {
                return (
                  <TooltipRahmen titel={`Warenausgang ${periodeLang(z.periode, gran)}`}>
                    <div style={{ color: FARBEN.tinteMittel }}>{leerText(z.periode, gran, importWochen, 'Keine Lieferung mit gemeldetem Termin in diesem Zeitraum.')}</div>
                  </TooltipRahmen>
                );
              }
              return (
                <TooltipRahmen titel={`Warenausgang ${periodeLang(z.periode, gran)}`}>
                  <Zeile farbe={FARBEN.gut} label="pünktlich" wert={String(z.puenktlich)} />
                  <Zeile farbe={FARBEN.warnung} label="1 Woche später" wert={String(z.spaet1)} />
                  <Zeile farbe={FARBEN.ernst} label="2–3 Wochen später" wert={String(z.spaet2bis3)} />
                  <Zeile farbe={FARBEN.kritisch} label="4+ Wochen später" wert={String(z.spaet4plus)} />
                  <div style={{ marginTop: '0.35rem', color: FARBEN.tinteMittel }}>
                    {vonText(z.puenktlich, z.gesamt)} hielten die letzte Meldung
                  </div>
                </TooltipRahmen>
              );
            }}
          />
          {/* stroke in Flaechenfarbe = die 2px-Luecke zwischen den Segmenten */}
          <Bar dataKey="puenktlich" stackId="t" fill={FARBEN.gut} maxBarSize={24} stroke={FARBEN.flaeche} strokeWidth={2} isAnimationActive={false} />
          <Bar dataKey="spaet1" stackId="t" fill={FARBEN.warnung} maxBarSize={24} stroke={FARBEN.flaeche} strokeWidth={2} isAnimationActive={false} />
          <Bar dataKey="spaet2bis3" stackId="t" fill={FARBEN.ernst} maxBarSize={24} stroke={FARBEN.flaeche} strokeWidth={2} isAnimationActive={false} />
          <Bar dataKey="spaet4plus" stackId="t" fill={FARBEN.kritisch} maxBarSize={24} stroke={FARBEN.flaeche} strokeWidth={2} radius={[3, 3, 0, 0]} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Bild 2: nur noch ZWEI Linien — letzte Meldung vs. erste Meldung. */}
      <ResponsiveContainer width="100%" height={190}>
        <ComposedChart data={zeilen} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
          {RASTER}
          <XAxis dataKey="label" {...ACHSE} scale="band" />
          <YAxis
            {...ACHSE} width={achsenBreite} domain={[0, 1]}
            ticks={[0, 0.25, 0.5, 0.75, 1]}
            tickFormatter={(v: number) => prozent(v)}
          />
          <Tooltip
            cursor={{ stroke: FARBEN.linie, strokeWidth: 1 }}
            content={({ active, payload }: any) => {
              if (!active || !payload?.length) return null;
              const z: TreueZeile = payload[0].payload;
              if (z.gesamt === 0 && z.ersteMessbar === 0) {
                return (
                  <TooltipRahmen titel={`Warenausgang ${periodeLang(z.periode, gran)}`}>
                    <div style={{ color: FARBEN.tinteMittel }}>{leerText(z.periode, gran, importWochen, 'Keine abgeschlossenen Aufträge in diesem Zeitraum.')}</div>
                  </TooltipRahmen>
                );
              }
              return (
                <TooltipRahmen titel={`Warenausgang ${periodeLang(z.periode, gran)}`}>
                  <Zeile farbe={FARBEN.serie1} label="letzte Meldung gehalten" wert={vonText(z.puenktlich, z.gesamt)} />
                  <Zeile farbe={FARBEN.serie2} label="schon die erste gehalten" wert={vonText(z.ersteGehalten, z.ersteMessbar)} />
                </TooltipRahmen>
              );
            }}
          />
          <Line
            dataKey="quoteLetzte" stroke={FARBEN.serie1} strokeWidth={2} type="linear" connectNulls
            dot={quotenPunkt(FARBEN.serie1, schmal)} activeDot={{ r: 5 }} isAnimationActive={false}
          />
          <Line
            dataKey="quoteErste" stroke={FARBEN.serie2} strokeWidth={2} strokeDasharray="5 4"
            type="linear" connectNulls dot={false} isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </>
  );
}

// ─── 2. Das 6-Wochen-Ziel ────────────────────────────────────────────────────

export function ZielDiagramm({ zeilen, gran, zielTage, importWochen }: { zeilen: ZielZeile[]; gran: Granularitaet; zielTage: number; importWochen: string[] }) {
  return (
    <ResponsiveContainer width="100%" height={220}>
      <ComposedChart data={zeilen} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        {RASTER}
        <XAxis dataKey="label" {...ACHSE} />
        <YAxis {...ACHSE} allowDecimals={false} width={34} />
        <Tooltip
          cursor={{ fill: 'rgba(26,26,26,0.04)' }}
          content={({ active, payload }: any) => {
            if (!active || !payload?.length) return null;
            const z: ZielZeile = payload[0].payload;
            if (z.gesamt === 0) {
              return (
                <TooltipRahmen titel={`Warenausgang ${periodeLang(z.periode, gran)}`}>
                  <div style={{ color: FARBEN.tinteMittel }}>{leerText(z.periode, gran, importWochen, 'Keine Lieferung mit bekannter AB in diesem Zeitraum.')}</div>
                </TooltipRahmen>
              );
            }
            return (
              <TooltipRahmen titel={`Warenausgang ${periodeLang(z.periode, gran)}`}>
                <Zeile farbe={FARBEN.gut} label={`innerhalb ${zahl(zielTage)} Tagen`} wert={String(z.imZiel)} />
                <Zeile farbe={FARBEN.kritisch} label="Ziel gerissen" wert={String(z.gerissen)} />
                <div style={{ marginTop: '0.35rem', color: FARBEN.tinteMittel }}>
                  {vonText(z.gerissen, z.gesamt)} über dem Ziel
                  {z.ohneAb > 0 ? ` · ${z.ohneAb} ohne auffindbare AB` : ''}
                </div>
              </TooltipRahmen>
            );
          }}
        />
        <Bar dataKey="imZiel" stackId="z" fill={FARBEN.gut} maxBarSize={24} stroke={FARBEN.flaeche} strokeWidth={2} isAnimationActive={false} />
        <Bar dataKey="gerissen" stackId="z" fill={FARBEN.kritisch} maxBarSize={24} stroke={FARBEN.flaeche} strokeWidth={2} radius={[3, 3, 0, 0]} isAnimationActive={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// ─── 3. Planstand und Prognose (bleibt auf KW-Ebene — Zukunft) ───────────────

export function PrognoseDiagramm({ zeilen }: { zeilen: PrognoseZeile[] }) {
  const mitBand = zeilen.map((z) => ({ ...z }));
  return (
    <ResponsiveContainer width="100%" height={260}>
      <ComposedChart data={mitBand} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
        {RASTER}
        <XAxis dataKey="label" {...ACHSE} />
        <YAxis {...ACHSE} allowDecimals={false} width={34} />
        <Tooltip
          cursor={{ fill: 'rgba(26,26,26,0.04)' }}
          content={({ active, payload }: any) => {
            if (!active || !payload?.length) return null;
            const p: PrognoseZeile = payload[0].payload;
            return (
              <TooltipRahmen titel={`Angekündigt für ${kwLang(p.woche)}`}>
                <Zeile farbe={FARBEN.serie1Hell} label="vom Lieferanten angekündigt" wert={`${p.plan} Aufträge`} />
                <Zeile farbe={FARBEN.serie1Dunkel} label="realistisch zu erwarten" wert={`etwa ${p.erwartet}`} />
                <div style={{ marginTop: '0.4rem', color: FARBEN.tinteMittel }}>{p.erklaerung}</div>
                {p.duenn && (
                  <div style={{ marginTop: '0.25rem', color: FARBEN.tinteLeise }}>
                    Nur {p.basis} {p.basis === 1 ? 'Vergleichsfall' : 'Vergleichsfälle'} — als Tendenz lesen.
                  </div>
                )}
              </TooltipRahmen>
            );
          }}
        />
        <Bar dataKey="plan" fill={FARBEN.serie1Hell} maxBarSize={24} radius={[3, 3, 0, 0]} isAnimationActive={false} />
        <Line
          dataKey="erwartet" stroke={FARBEN.serie1Dunkel} strokeWidth={2} type="linear"
          dot={{ r: 4, fill: FARBEN.serie1Dunkel, stroke: FARBEN.flaeche, strokeWidth: 2 }}
          activeDot={{ r: 5 }} isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// ─── 4. Verschiebungen ───────────────────────────────────────────────────────

export function VerschiebungenDiagramm({ zeilen, achse, gran }: {
  zeilen: VerschiebungZeile[]; achse: VerschiebungAchse; gran: Granularitaet;
}) {
  return (
    <ResponsiveContainer width="100%" height={220}>
      <ComposedChart data={zeilen} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} stackOffset="sign">
        {RASTER}
        <XAxis dataKey="label" {...ACHSE} />
        <YAxis
          {...ACHSE} width={34} domain={[achse.unten, achse.oben]} allowDecimals={false}
          ticks={achse.ticks}
          tickFormatter={(v: number) => String(Math.abs(v))}
        />
        <ReferenceLine y={0} stroke={FARBEN.tinteLeise} strokeWidth={1} />
        <Tooltip
          cursor={{ fill: 'rgba(26,26,26,0.04)' }}
          content={({ active, payload }: any) => {
            if (!active || !payload?.length) return null;
            const v: VerschiebungZeile = payload[0].payload;
            return (
              <TooltipRahmen titel={`Umgeplant in ${periodeLang(v.periode, gran)}`}>
                <Zeile farbe={FARBEN.kritisch} label="nach hinten" wert={String(v.nachHinten)} />
                <Zeile farbe={FARBEN.gut} label="nach vorne" wert={String(v.nachVorne)} />
                {v.nachHinten === 0 && v.nachVorne === 0 && (
                  <div style={{ marginTop: '0.35rem', color: FARBEN.tinteMittel }}>
                    Ruhig — kein Termin wurde umgeplant.
                  </div>
                )}
              </TooltipRahmen>
            );
          }}
        />
        <Bar dataKey="nachHinten" stackId="v" fill={FARBEN.kritisch} maxBarSize={24} radius={[3, 3, 0, 0]} isAnimationActive={false} />
        <Bar dataKey="nachVorneNegativ" stackId="v" fill={FARBEN.gut} maxBarSize={24} radius={[0, 0, 3, 3]} isAnimationActive={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// ─── 5. Wartezeit (AB → Warenausgang) ────────────────────────────────────────

export function VorlaufDiagramm({ zeilen, gran }: { zeilen: VorlaufZeile[]; gran: Granularitaet }) {
  // Beschriftet wird die juengste Periode, die ueberhaupt einen Wert hat.
  const letzterIndex = zeilen.reduce((idx, z, i) => (z.medianTage !== null ? i : idx), -1);
  return (
    <ResponsiveContainer width="100%" height={200}>
      <ComposedChart data={zeilen} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
        {RASTER}
        <XAxis dataKey="label" {...ACHSE} />
        <YAxis {...ACHSE} width={44} allowDecimals={false} tickFormatter={(v: number) => `${v} T.`} />
        <Tooltip
          cursor={{ fill: 'rgba(26,26,26,0.04)' }}
          content={({ active, payload }: any) => {
            if (!active || !payload?.length) return null;
            const v: VorlaufZeile = payload[0].payload;
            if (v.medianTage === null) {
              return (
                <TooltipRahmen titel={periodeLang(v.periode, gran)}>
                  <div style={{ color: FARBEN.tinteMittel }}>Keine Lieferung mit bekannter AB in diesem Zeitraum.</div>
                </TooltipRahmen>
              );
            }
            return (
              <TooltipRahmen titel={periodeLang(v.periode, gran)}>
                <Zeile farbe={FARBEN.serie1} label="typische Wartezeit" wert={tageMitWochen(v.medianTage)} />
                <Zeile label="ausgewertete Aufträge" wert={String(v.n)} />
              </TooltipRahmen>
            );
          }}
        />
        <Bar dataKey="medianTage" fill={FARBEN.serie1} maxBarSize={24} radius={[3, 3, 0, 0]} isAnimationActive={false}>
          <LabelList
            dataKey="medianTage"
            content={({ x, y, width, value, index }: any) => {
              if (index !== letzterIndex || x === undefined || value === null) return null;
              return (
                <text
                  x={Number(x) + Number(width) / 2} y={Number(y) - 6}
                  textAnchor="middle" fontSize={11} fill={FARBEN.tinteMittel} fontWeight={600}
                >
                  {`${zahl(Number(value))} Tage`}
                </text>
              );
            }}
          />
        </Bar>
      </ComposedChart>
    </ResponsiveContainer>
  );
}
